import { Box, Button, Paper } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import CustomToolbar from "../components/CustomToolbar"
import DialogForm from "../components/DialogForm"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import { useQsFilters } from "../hooks/filters"
import { Tag } from "../types"
import { Tag as TagIcon } from "@mui/icons-material"
import { useSnackbarMessage } from "../hooks/snackbar"
import DialogConfirm from "../components/DialogConfirm"
import { useTagCreate, useTagDelete, useTagUpdate, useTagsList, useUnificaTags } from "../hooks/tags"
import TagForm from "../components/forms/TagForm"
import useUserCanWriteAndDelete from "../hooks/permissions"
import IconButtonDelete from "../components/IconButtonDelete"
import IconButtonEdit from "../components/IconButtonEdit"
import UnificaTagsForm from "../components/forms/UnificaTagsForm"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "nome_it",
})

export default function Tags() {
  const pageSize = usePageSize()
  const { filters, setFilters, setFiltersDebounced, uiFilters } = useQsFilters(initFilters)
  const { t } = useTranslation()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const [modalStateUnifica, modalActionsUnifica] = useModalTrigger()

  const columns: GridColDef[] = useMemo(() => {
    let cols: GridColDef[] = []
    cols = [
      {
        field: "nome_it",
        headerName: t("tag.nome_it"),
        flex: 1,
      },
      {
        field: "nome_en",
        headerName: t("tag.nome_en"),
        flex: 1,
      },
    ]
    if (userCanWriteAndDelete) {
      cols.push({
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) =>
          userCanWriteAndDelete && (
            <>
              <IconButtonEdit onClick={() => editModalActions.open(params.row)} />
              <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} />
            </>
          ),
      })
    }
    return cols
  }, [t, userCanWriteAndDelete])

  const { data, isLoading, refetch } = useTagsList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
  })

  const { mutateAsync: create } = useTagCreate()
  const { mutateAsync: update } = useTagUpdate()
  const { mutateAsync: remove } = useTagDelete()

  const { mutateAsync: unifica } = useUnificaTags()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const [createModalState, createModalActions] = useModalTrigger()
  const [editModalState, editModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0

    return count
  }, [])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle
            title={t("tag.titolo_lista")}
            breadcrumbs={[
              {
                label: t("tag.tags"),
                iconType: TagIcon,
              },
            ]}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            {userCanWriteAndDelete && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  modalActionsUnifica.open()
                }}
              >
                {t("tag.unifica")}
              </Button>
            )}
            {userCanWriteAndDelete && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  createModalActions.open()
                }}
              >
                {t("tag.cta_aggiungi")}
              </Button>
            )}
          </Box>
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1 })}
              filtersCount={filtersCount}
              ref={toolbarRef}
            />
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Tag>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("tag.cta_aggiungi")}
        FormComponent={TagForm}
        save={(values: Tag) => {
          return create(values).then(() => {
            createModalActions.close()
          })
        }}
      />
      <DialogForm<Tag>
        open={editModalState.isOpen}
        handleClose={editModalActions.close}
        title={t("tag.cta_modifica")}
        FormComponent={TagForm}
        save={(values: Tag) => {
          return update(values).then(() => {
            editModalActions.close()
            showMessage(t("tag.esito_modifica_ok"))
          })
        }}
        item={editModalState.value}
      />
      <DialogForm<any>
        open={modalStateUnifica.isOpen}
        handleClose={modalActionsUnifica.close}
        title={t("tag.unifica")}
        FormComponent={UnificaTagsForm}
        save={(values) => {
          return unifica(values)
            .then(() => {
              modalActionsUnifica.close()
              showMessage(t("tag.esito_unifica_ok"))
              refetch()
            })
            .finally(() => {
              modalActionsUnifica.close()
              refetch()
              showMessage(t("tag.esito_unifica_ok"))
            })
        }}
      />

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("tag.cta_elimina")}
        subtitle={
          <span>
            {t("tag.cta_subtitle_elimina")} <b>{deleteModalState.value?.nome_it}</b> ?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("tag.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
